<template>
  <div class="bg-white rounded-2xl p-7 border border-[#1c4848]/10 shadow-sm" :class="[wrapperClass]">
    <div class="flex items-start justify-between mb-6 gap-4">
      <div>
        <h3 class="text-[20px] font-medium tracking-tight text-[#1c4848]">{{ title }}</h3>
        <p class="text-base text-[#1c4848]/60 mt-1 leading-relaxed">{{ subtitle }}</p>
      </div>
      <span v-if="rows.length" class="text-[13px] font-medium px-2 py-1 rounded-lg text-[#1c4848] bg-[#1c4848]/5 shrink-0">
        {{ formatNumber(totals.views) }} views · {{ formatNumber(totals.purchases) }} paid
      </span>
    </div>

    <p v-if="!rows.length" class="text-base text-[#1c4848]/40 py-8 text-center">
      No paywall views recorded in this period.
    </p>

    <div v-else class="overflow-x-auto -mx-2">
      <table class="w-full min-w-[760px]">
        <thead>
          <tr class="text-left">
            <th class="px-2 pb-3 text-[13px] font-semibold text-[#1c4848]/45">Where</th>
            <th v-for="col in columns" :key="col.key" class="px-2 pb-3 text-[13px] font-semibold text-[#1c4848]/45 text-right" :title="col.help">
              {{ col.label }}
            </th>
            <th class="px-2 pb-3 text-[13px] font-semibold text-[#1c4848]/45 text-right">Paid / seen</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="row.source" class="border-t border-[#1c4848]/5">
            <td class="px-2 py-4">
              <p class="text-lg font-medium text-[#1c4848]">{{ labelFor(row.source) }}</p>
              <p class="text-[13px] text-[#1c4848]/40">{{ formatNumber(row.viewers) }} {{ row.viewers === 1 ? 'person' : 'people' }} saw it</p>
            </td>
            <td v-for="col in columns" :key="col.key" class="px-2 py-4 text-right">
              <p class="text-[19px] font-semibold text-[#1c4848] tabular-nums" :class="col.key === 'purchases' && row.purchases > 0 ? 'text-emerald-600' : ''">
                {{ formatNumber(row[col.key]) }}
              </p>
              <p class="text-[13px] text-[#1c4848]/40 tabular-nums">{{ pct(row[col.key], row.views) }}</p>
            </td>
            <td class="px-2 py-4 text-right">
              <span class="text-[13px] font-medium px-2 py-1 rounded-lg" :class="row.purchases > 0 ? 'text-emerald-600 bg-emerald-50' : 'text-[#1c4848]/40 bg-[#1c4848]/5'">
                {{ row.conversion_rate }}%
              </span>
            </td>
          </tr>
          <tr class="border-t-2 border-[#1c4848]/10">
            <td class="px-2 py-4 text-lg font-bold text-[#1c4848]">All paywalls</td>
            <td v-for="col in columns" :key="col.key" class="px-2 py-4 text-right">
              <p class="text-[19px] font-semibold text-[#1c4848] tabular-nums">{{ formatNumber(totals[col.key]) }}</p>
              <p class="text-[13px] text-[#1c4848]/40 tabular-nums">{{ pct(totals[col.key], totals.views) }}</p>
            </td>
            <td class="px-2 py-4 text-right">
              <span class="text-[13px] font-medium px-2 py-1 rounded-lg text-[#1c4848] bg-[#ecd79f]/40">{{ pct(totals.purchases, totals.views) }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <p class="mt-5 text-[13px] text-[#1c4848]/50 leading-relaxed">
      Seen = the paywall was shown. Closed = dismissed without buying. Tapped a plan = the App Store purchase sheet was opened.
      Cancelled = closed the App Store sheet. Paid = purchase completed (a free trial counts as paid here; trials vs. paid are split above).
    </p>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  title: { type: String, default: 'What happens at the paywall' },
  subtitle: { type: String, default: 'Every paywall, by where it was shown: how many saw it, closed it, tapped a plan, gave up in the App Store sheet, or paid.' },
  rows: { type: Array, default: () => [] },
  labelFor: { type: Function, default: (value) => value },
  wrapperClass: { type: String, default: '' },
});

const columns = [
  { key: 'views', label: 'Seen', help: 'paywall_view' },
  { key: 'dismissed', label: 'Closed', help: 'paywall_dismiss' },
  { key: 'started', label: 'Tapped a plan', help: 'purchase_start' },
  { key: 'cancelled', label: 'Cancelled', help: 'purchase_cancel' },
  { key: 'failed', label: 'Failed', help: 'purchase_failed' },
  { key: 'purchases', label: 'Paid', help: 'purchase_success' },
];

const totals = computed(() => {
  const sum = (key) => props.rows.reduce((acc, row) => acc + (Number(row[key]) || 0), 0);
  return { views: sum('views'), dismissed: sum('dismissed'), started: sum('started'), cancelled: sum('cancelled'), failed: sum('failed'), purchases: sum('purchases') };
});

const formatNumber = (value) => new Intl.NumberFormat('en-US').format(value ?? 0);
const pct = (part, whole) => (whole > 0 ? `${Math.round((Number(part) / whole) * 1000) / 10}%` : '—');
</script>
